
import {
  IngestBox, IngestTextFragment, IngestFragmentBox,
} from '@/model';
import LabeledText from '@/components/LabeledText.vue';
import { distinct } from '@/util/array-util';
import { traverse } from '@/util/functional-iterator';
import { GroupedInterval } from '@/util/text-selection';
import Vue from 'vue';

type Item = { id: number; from?: number; to?: number; labels: number[]; first: boolean }

export default Vue.extend({
  name: 'DocumentTextBox',
  components: { LabeledText },

  props: {
    box: { type: Object as () => IngestBox, required: true },
    fragments: { type: Array as () => IngestTextFragment[], default: () => [] },
    highlights: { type: Array as () => IngestFragmentBox[] | undefined },
    shown: { type: Boolean, default: false },
    colors: { type: Boolean, default: true },
    zoom: { type: Number },
  },

  data() {
    return {
      renderedActual: false,
      hover: [] as number[],
      clips: [] as GroupedInterval<Item>[],
    };
  },

  watch: {
    shown: {
      handler(v, prev) {
        if (v && !prev && !this.renderedActual) {
          this.$nextTick(() => {
            // clips with actual actual top rendered
            this.renderedActual = true;
            this.emitRendered();
          });
        }
      },
    },
    zoom() {
      this.$nextTick(() => {
        this.emitRendered();
      });
    },
  },

  computed: {
    fragmentItems(): Array<Item> {
      return [...this.fragments
        .flatMap((f) => {
          const bs: Item[] = [];
          for (const [i, box] of f.boxes.entries()) {
            if (box.boxId === this.box.id) {
              bs.push({
                id: f.id,
                from: box.from,
                to: box.to,
                labels: f.labels.map((la) => la.labelId),
                first: i === 0,
              });
            }
          }
          return bs;
        })];
    },

    items(): Array<Item> {
      const hs = this.highlights !== undefined ? this.highlights
        .filter((h) => h.boxId === this.box.id)
        .map((h) => ({
          id: 0,
          from: h.from,
          to: h.to,
          labels: [],
          first: false,
        })) : [];
      return [...this.fragmentItems, ...hs];
    },
    containerTop(): number | undefined {
      const box = traverse(this.$el, (e) => e.parentElement || undefined).find((e) => e.classList.contains('box'));
      return this.elementTop(box);
    },
  },

  methods: {
    clipsChanged(clips: GroupedInterval<Item>[]) {
      this.clips = clips;
      this.$nextTick(() => {
        this.emitRendered();
      });
    },
    setHover(value: number | undefined, add: boolean) {
      if (value) {
        if (add) {
          this.hover = distinct([...this.hover, value]);
        } else {
          this.hover = this.hover.filter((h) => h !== value);
        }
      }
    },
    emitRendered() {
      const clips = (this.$refs.text as Vue).$refs.clips as Element[];
      const rendered = clips
        .flatMap((elem) => {
          const ci = parseInt(elem.attributes.getNamedItem('clipIndex')?.value as string, 10);
          const clip = this.clips[ci];
          if (!clip) {
            return [];
          }
          const top = ((this.shown ? this.elementTop(elem) : this.containerTop) || 0);
          return clip.group
            .filter((f) => f.id !== undefined)
            .map((f) => ({
              id: f.id,
              top, // dom y-position
              first: f.first, // first box in fragment
              actual: this.shown, // the text intervals has been rendered in their actual dom position
              hover: (value: boolean) => this.setHover(f.id, value),
            }));
        });
      if (rendered.length > 0) {
        this.$emit('fragments-rendered', rendered);
      }
    },
    elementTop(elem?: Element): number | undefined {
      const rect = elem?.getBoundingClientRect();
      if (!rect || rect.top === 0) {
        return undefined;
      }
      return rect.top + window.pageYOffset;
    },
  },
});
